/**
 * RunnerPolicyForm — single editor reused at the three policy layers.
 *
 * `layer` controls inheritance UX:
 *  - 'global': all fields define values (no inherit).
 *  - 'profile' | 'runner': empty values mean "inherit from the layer below".
 */

import { useState } from 'react';
import { Button, Card } from '@/components/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { ApprovalMode, ApprovalScope, RunnerPolicyConfig } from '@/features/mcp/hooks/usePolicyLayers';

interface RunnerPolicyFormProps {
  value: RunnerPolicyConfig;
  onChange: (next: RunnerPolicyConfig) => void;
  layer: 'global' | 'profile' | 'runner';
  disabled?: boolean;
}

const SUGGESTED_KEYWORDS = ['rm', 'truncate', 'shred', 'rmtree', 'shutil', 'delete', 'drop', 'force', '-delete', 'mkfs'];

function ChipList({
  items,
  onChange,
  suggestions,
  disabled,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (!value || items.includes(value)) return;
    onChange([...items, value]);
    setDraft('');
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span key={item} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs">
            {item}
            {!disabled && (
              <button type="button" aria-label={`Quitar ${item}`} onClick={() => onChange(items.filter((i) => i !== item))}>
                <Trash2 size={12} className="text-muted-foreground hover:text-red-500" />
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-1.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder="Añadir y pulsar Enter"
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm"
          />
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus size={14} /> Añadir
          </Button>
        </div>
      )}
      {suggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions
            .filter((s) => !items.includes(s))
            .map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChange([...items, s])}
                className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
              >
                + {s}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function TextList({
  items,
  onChange,
  placeholder,
  disabled,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (!value || items.includes(value)) return;
    onChange([...items, value]);
    setDraft('');
  };
  return (
    <div className="space-y-1.5">
      {items.length > 0 && (
        <ul className="text-sm text-foreground/80 list-disc pl-4">
          {items.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <span className="font-mono text-xs">{item}</span>
              {!disabled && (
                <button type="button" aria-label="Quitar" onClick={() => onChange(items.filter((i) => i !== item))}>
                  <Trash2 size={12} className="text-muted-foreground hover:text-red-500" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <div className="flex gap-1.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder={placeholder ?? 'Añadir y pulsar Enter'}
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm font-mono text-xs"
          />
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus size={14} /> Añadir
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </div>
  );
}

function TriStateToggle({
  label,
  value,
  onChange,
  allowInherit,
  disabled,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  allowInherit: boolean;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-sm">
      <span>{label}</span>
      {disabled ? (
        <span className="text-xs text-muted-foreground">{value === true ? 'Sí' : value === false ? 'No' : '—'}</span>
      ) : (
        <select
          value={value === undefined ? 'inherit' : String(value)}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === 'inherit' ? undefined : v === 'true');
          }}
          className="rounded border border-border bg-background px-2 py-1 text-sm"
        >
          {allowInherit && <option value="inherit">Heredar</option>}
          <option value="true">Sí</option>
          <option value="false">No</option>
        </select>
      )}
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  allowInherit,
  disabled,
  min = 0,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  allowInherit: boolean;
  disabled?: boolean;
  min?: number;
}) {
  return (
    <div className="space-y-1">
      <span className="text-sm font-medium">{label}</span>
      <input
        type="number"
        min={min}
        disabled={disabled}
        value={value ?? ''}
        placeholder={allowInherit ? 'Heredar' : '0'}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? undefined : Math.max(min, Number(raw)));
        }}
        className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
      />
    </div>
  );
}

export function RunnerPolicyForm({ value, onChange, layer, disabled }: RunnerPolicyFormProps) {
  const allowInherit = layer !== 'global';
  const set = (patch: Partial<RunnerPolicyConfig>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Modo de aprobación</h3>
        <div className="flex gap-2">
          {(['explicit_approval', 'default', 'bypass'] as ApprovalMode[]).map((mode) => (
            <label key={mode} className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                name={`mode-${layer}`}
                checked={value.mode === mode}
                disabled={disabled}
                onChange={() => set({ mode })}
              />
              {mode === 'explicit_approval' && 'Aprobar todo'}
              {mode === 'default' && 'Por defecto'}
              {mode === 'bypass' && 'Bypass'}
            </label>
          ))}
          {allowInherit && (
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <input
                type="radio"
                name={`mode-${layer}`}
                checked={value.mode === undefined}
                disabled={disabled}
                onChange={() => set({ mode: undefined })}
              />
              Heredar
            </label>
          )}
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Aprobación</h3>
        <Field label="Palabras clave que requieren aprobación">
          <ChipList
            items={value.approval_required_for ?? []}
            onChange={(items) => set({ approval_required_for: items.length ? items : undefined })}
            suggestions={SUGGESTED_KEYWORDS}
            disabled={disabled}
          />
        </Field>
        <Field label="Patrones regex (RE2) que requieren aprobación">
          <TextList
            items={value.approval_required_patterns ?? []}
            onChange={(items) => set({ approval_required_patterns: items.length ? items : undefined })}
            placeholder="ej: kubectl\s+delete"
            disabled={disabled}
          />
        </Field>
        <Field label="Excepciones (regex exentas de aprobación)">
          <TextList
            items={value.approval_exceptions ?? []}
            onChange={(items) => set({ approval_exceptions: items.length ? items : undefined })}
            placeholder="ej: ^rm -f /tmp/safe-"
            disabled={disabled}
          />
        </Field>
        <Field label="Ámbito de la aprobación">
          <select
            value={value.approval_scope ?? 'inherit'}
            disabled={disabled}
            onChange={(e) =>
              set({
                approval_scope:
                  e.target.value === 'inherit' ? undefined : (e.target.value as ApprovalScope),
              })
            }
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          >
            {allowInherit && <option value="inherit">Heredar</option>}
            <option value="all">Siempre</option>
            <option value="outside_allowed_paths">Solo fuera de los directorios permitidos</option>
          </select>
        </Field>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Comandos</h3>
        <Field label="Herramientas bloqueadas">
          <TextList
            items={value.blocked_tool_names ?? []}
            onChange={(items) => set({ blocked_tool_names: items.length ? items : undefined })}
            disabled={disabled}
          />
        </Field>
        <Field label="Comandos base bloqueados">
          <TextList
            items={value.blocked_commands ?? []}
            onChange={(items) => set({ blocked_commands: items.length ? items : undefined })}
            disabled={disabled}
          />
        </Field>
        <Field label="Comandos base permitidos (whitelist)">
          <TextList
            items={value.allowed_commands ?? []}
            onChange={(items) => set({ allowed_commands: items.length ? items : undefined })}
            disabled={disabled}
          />
        </Field>
        <Field label="Patrones bloqueados">
          <TextList
            items={value.blocked_patterns ?? []}
            onChange={(items) => set({ blocked_patterns: items.length ? items : undefined })}
            disabled={disabled}
          />
        </Field>
      </Card>

      <Card className="p-4 space-y-2">
        <h3 className="font-semibold">Seguridad</h3>
        <TriStateToggle label="Bloquear sudo" value={value.no_sudo} onChange={(v) => set({ no_sudo: v })} allowInherit={allowInherit} disabled={disabled} />
        <TriStateToggle label="Bloquear interactivos" value={value.no_interactive} onChange={(v) => set({ no_interactive: v })} allowInherit={allowInherit} disabled={disabled} />
        <TriStateToggle label="Bloquear pipe a shell" value={value.no_pipe_to_shell} onChange={(v) => set({ no_pipe_to_shell: v })} allowInherit={allowInherit} disabled={disabled} />
        <TriStateToggle label="Bloquear background (&)" value={value.no_background} onChange={(v) => set({ no_background: v })} allowInherit={allowInherit} disabled={disabled} />
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Directorios de trabajo</h3>
        <Field label="Directorios permitidos">
          <TextList
            items={value.allowed_paths ?? []}
            onChange={(items) => set({ allowed_paths: items.length ? items : undefined })}
            placeholder="ej: ~/projects"
            disabled={disabled}
          />
        </Field>
        <Field label="Directorios bloqueados">
          <TextList
            items={value.blocked_paths ?? []}
            onChange={(items) => set({ blocked_paths: items.length ? items : undefined })}
            placeholder="ej: ~/secrets"
            disabled={disabled}
          />
        </Field>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">Límites</h3>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Timeout máx (s)" value={value.max_timeout_seconds} onChange={(v) => set({ max_timeout_seconds: v })} allowInherit={allowInherit} disabled={disabled} />
          <NumberField label="Salida máx (bytes)" value={value.max_output_size} onChange={(v) => set({ max_output_size: v })} allowInherit={allowInherit} disabled={disabled} />
          <NumberField label="Rate por minuto" value={value.rate_limit_per_minute} onChange={(v) => set({ rate_limit_per_minute: v })} allowInherit={allowInherit} disabled={disabled} />
          <NumberField label="Rate por hora" value={value.rate_limit_per_hour} onChange={(v) => set({ rate_limit_per_hour: v })} allowInherit={allowInherit} disabled={disabled} />
        </div>
      </Card>
    </div>
  );
}

export default RunnerPolicyForm;
